// import fetch from 'node-fetch';
import jwt from 'jsonwebtoken';

import * as I from '@/interfaces';
import { AxStorage } from './storage';
import { read } from 'fs';
import { resolve } from 'path';

export type Method =
  | 'get'
  | 'GET'
  | 'delete'
  | 'DELETE'
  | 'head'
  | 'HEAD'
  | 'options'
  | 'OPTIONS'
  | 'post'
  | 'POST'
  | 'put'
  | 'PUT'
  | 'patch'
  | 'PATCH'
  | 'link'
  | 'LINK'
  | 'unlink'
  | 'UNLINK';

export class AxAPIError extends Error {
  __proto__: Error;

  status?: number;

  get name() {
    return this.constructor.name;
  }
  constructor(status: number, message?: string) {
    const trueProto = new.target.prototype;
    super(message);

    this.__proto__ = trueProto;
    this.status = status;
  }
}

export class AxAPIHTTPGenericError extends AxAPIError {}
export class AxAPIBadRequestError extends AxAPIHTTPGenericError {}
export class AxAPIForbiddenError extends AxAPIHTTPGenericError {}
export class AxAPINotFoundError extends AxAPIHTTPGenericError {}
export class AxAPIUnauthorizedError extends AxAPIHTTPGenericError {}
export class AxAPIMethodNotAllowedError extends AxAPIHTTPGenericError {}
export class AxAPINotAcceptableError extends AxAPIHTTPGenericError {}
export class AxAPIUnsupportedMediaTypeError extends AxAPIHTTPGenericError {}

export class AxAPIServerError extends AxAPIError {}
export class AxAPIResultNotOKError extends AxAPIServerError {}
export class AxAPIResponseEmptyError extends AxAPIServerError {}

export class AxAPIRequestError extends AxAPIError {}

export enum TokenStatus {
  Ok,
  NotExist,
  InvalidToken,
  EncryptedToken,
  Expired
}

export interface TokenData {
  header: {
    alg: string;
    typ: string;
  };

  payload: {
    nameid: string;
    nbf: number;
    exp: number;
    iat: number;
    iss: string;
    aud: string;
  };

  signature: string;
}

function validateToken() {
  const token = AxStorage.axToken;
  if (!token) return TokenStatus.NotExist;

  const decoded = jwt.decode(token, { complete: true });

  if (decoded) {
    const now = Date.now();
    const milli = 1000;

    // decoded 토큰이 string일 때는 암호화된 토큰일때만
    if (typeof decoded === 'string') return TokenStatus.EncryptedToken;

    const exp = decoded.payload.exp * milli;
    if (now >= exp) {
      return TokenStatus.Expired;
    }
  } else {
    return TokenStatus.InvalidToken;
  }
  return TokenStatus.Ok;
}

function fetchTokenInfo() {
  const token = AxStorage.axToken;
  if (!token) return null;

  const decoded = jwt.decode(token, { complete: true }) as TokenData;
  return decoded;
}

function makeHeader(isFormData?: boolean) {
  const obj: StringMap = {};

  const token = AxStorage.axToken;
  if (validateToken() === TokenStatus.Ok) {
    const decoded = jwt.decode(token, { complete: true });

    if (decoded) {
      const now = Date.now();
      const milli = 1000;

      // decoded 토큰이 string일 때는 암호화된 토큰일때만
      if (typeof decoded === 'string') return;

      const exp = decoded.payload.exp * milli;

      //c#에서 오는 tick이랑 js tick 의 자리수가 다름
      if (now < exp) {
        obj['Authorization'] = 'Bearer ' + token;
      }
      console.log('Bearer ' + token);
    }
  }
  if (isFormData) {
    return Object.assign(obj, {
      'Access-Control-Allow-Origin': '*'
    });
  } else {
    return Object.assign(obj, {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json; x-www-form-urlencoded; encoding=utf-8'
    });
  }
}

type StringMap = { [key: string]: string };

interface APIReq<TReq> {
  method?: Method;
  headers?: StringMap;
  data?: TReq;
}

interface ApiResp<T> {
  result: number;
  data: T;
  errorMsg: string;
  token?: string;
}

export default class AxAPI {
  async request<TReq, TResp>(
    url: string,
    method: Method,
    params?: APIReq<TReq>
  ): Promise<ApiResp<TResp> | AxAPIError> {
    const body = params && params.data ? { body: JSON.stringify(params?.data) } : {};
    const options = {
      headers: { ...makeHeader(), ...params?.headers },
      method,
      ...body
    };

    const { hostname } = window.location;
     //const baseURL = process.env.NODE_ENV === 'development' ? `http://203.233.219.147:2272` : '';
    const baseURL = process.env.NODE_ENV === 'development' ? `https://${hostname}:44393` : '';
    const reqURL = baseURL + url;

    try {
      const resp = await fetch(reqURL, options);
      const { status, statusText } = resp;

      if (!resp.ok) {
        switch (resp.status) {
          case 400:
            throw new AxAPIBadRequestError(status, statusText);
          case 401:
            throw new AxAPIUnauthorizedError(status, statusText);
          case 403:
            throw new AxAPIForbiddenError(status, statusText);
          case 404:
            throw new AxAPINotFoundError(status, statusText);
          case 405:
            throw new AxAPIMethodNotAllowedError(status, statusText);
          case 406:
            throw new AxAPINotAcceptableError(status, statusText);
          case 415:
            throw new AxAPIUnsupportedMediaTypeError(status, statusText);
          default:
            throw new AxAPIHTTPGenericError(status, statusText);
        }
      }

      const json = await resp.json();
      if (!json.data) {
        throw new AxAPIResponseEmptyError(json.result, json.errorMsg);
      } else if (json.result !== 1) {
        throw new AxAPIServerError(json.result, json.errorMsg);
      }

      if (json.token) {
        AxStorage.axToken = json.token;
      }
      return json;
    } catch (e) {
      if (e instanceof AxAPIError) {
        return e;
      } else if (e instanceof DOMException || e instanceof Error) {
        return new AxAPIRequestError(-1, e.message);
      } else {
        return e;
      }
    }
  }

  readFile(file: File): Promise<Event> {
    const read: FileReader = new FileReader();
    // read.onloadend = function(e: Event) {
    //   console.log('read end');
    //   console.log(e);
    // };
    read.readAsArrayBuffer(file);
    return new Promise<Event>((resolve, reject) => {
      read.onloadend = function(ee: Event) {
        console.log('promise end');
        console.log(ee);
        resolve(ee);
      };
    });
  }

  async requestFile<TResp>(
    url: string,
    method: Method,
    formData: FormData
  ): Promise<ApiResp<TResp> | AxAPIError> {
    const { hostname } = window.location;
    const baseURL = process.env.NODE_ENV === 'development' ? `https://${hostname}:44393` : '';
    const reqURL = baseURL + url;

    const options: RequestInit = {
      headers: { ...makeHeader(true) },
      method,
      body: formData
    };

    try {
      const resp = await fetch(reqURL, options);
      const { status, statusText } = resp;

      if (!resp.ok) {
        switch (resp.status) {
          case 400:
            throw new AxAPIBadRequestError(status, statusText);
          case 401:
            throw new AxAPIUnauthorizedError(status, statusText);
          case 403:
            throw new AxAPIForbiddenError(status, statusText);
          case 404:
            throw new AxAPINotFoundError(status, statusText);
          case 405:
            throw new AxAPIMethodNotAllowedError(status, statusText);
          case 406:
            throw new AxAPINotAcceptableError(status, statusText);
          case 415:
            throw new AxAPIUnsupportedMediaTypeError(status, statusText);
          default:
            throw new AxAPIHTTPGenericError(status, statusText);
        }
      }

      const json = await resp.json();
      if (!json.data) {
        throw new AxAPIResponseEmptyError(json.result, json.errorMsg);
      } else if (json.result !== 1) {
        throw new AxAPIServerError(json.result, json.errorMsg);
      }

      if (json.token) {
        AxStorage.axToken = json.token;
      }
      return json;
    } catch (e) {
      if (e instanceof AxAPIError) {
        return e;
      } else if (e instanceof DOMException || e instanceof Error) {
        return new AxAPIRequestError(-1, e.message);
      } else {
        return e;
      }
    }
  }

  async get<TNull, TResp>(url: string, params?: APIReq<never>) {
    return this.request<{}, TResp>(url, 'GET', params);
  }

  async post<TReq, TResp>(url: string, params: APIReq<TReq>) {
    return this.request<TReq, TResp>(url, 'POST', params);
  }

  async postFile<TResp>(url: string, formData: FormData) {
    return this.requestFile<TResp>(url, 'POST', formData);
  }

  validateToken = validateToken;
  fetchTokenInfo = fetchTokenInfo;

  get call() {
    return {
      login: (data: I.PacketLogin) =>
        this.post<I.PacketLogin, I.Account>('/api/Login/Login', {
          data,
          headers: { Authorization: '' } /* 로그인은 Authorization 토큰이 가면 안됨 */
        }),
      logout: () => this.get<{}, I.Account>('/api/Login/LogOut'),
      schedule: {
        get: () => this.get<{}, I.UserScheduleList[]>('/api/Auth/Schedule'),
        scheduleUpdate: (data: I.PacketUserScheduleUpdate) =>
          this.post<I.PacketUserScheduleUpdate, I.UserScheduleList>('/api/Auth/ScheduleUpdate', {
            data
          })
      },
      user: {
        userBatchUpdate: (data: I.PacketUserBatchUpdate) =>
          this.post<I.PacketUserBatchUpdate, I.User[]>('/api/User/BatchUpdate', {
            data
          }),
        userSearchPageInfo: () => this.get<{}, I.UserSearchPageInfo>('/api/User/Search'),
        searchDetail: (data: I.PacketUserSearchDetail) =>
          this.post<I.PacketUserSearchDetail, I.UserSearchResult>('/api/User/SearchDetail', {
            data
          }),
        search: (data: I.PacketUserSearchDetail) =>
          this.post<I.PacketUserSearchDetail, I.UserSearchResult>('/api/User/Search', {
            data
          }),
        getUserUpdatePageInfo: (userID: number) =>
          this.get<{}, I.UesrUpdatePageInfo>('/api/User/SearchByUserID?userID=' + userID),
        getRegisterPageInfo: () => this.get<{}, I.UserRegisterPageInfo>('/api/User/Register'),
        register: (data: I.UserRegisterReq) =>
          this.post<I.UserRegisterReq, I.UserRegisterResult>('/api/User/Register', {
            data
          }),
        update: (data: I.UserRegisterReq) =>
          this.post<I.UserRegisterReq, I.UserRegisterResult>('/api/User/Update', {
            data
          }),
        photo: (formData: FormData) => {
          this.postFile<I.User>('/api/User/Photo', formData);
        },
        getUserHistoryPageInfo: () => this.get<{}, I.UserHistoryPageInfo>('/api/User/History'),
        historySearch: (data: I.PacketHistorySearch) =>
          this.post<I.PacketHistorySearch, I.History[]>('/api/User/HistorySearch', {
            data
          }),
        history: (userid: number) =>
          this.get<{}, I.History[]>('/api/User/HistoryFromID?userid=' + userid),
        userInfo: (search: string) =>
          this.get<{}, I.UserInfo[]>('/api/User/UserInfo?search=' + search),
        statusSearch: (data: I.PacketUserStatusSearch) =>
          this.post<I.PacketUserStatusSearch, I.History[]>('/api/User/Status', {
            data
          }),
        statusAllSearch: (data: I.PacketUserStatusSearch) =>
          this.post<I.PacketUserStatusSearch, I.UserStatusAllData[]>('/api/User/UserStatusAll', {
            data
          }),
        typeAllSearch: (data: I.PacketUserStatusSearch) =>
          this.post<I.PacketUserStatusSearch, I.UserTypeAllData[]>('/api/User/UsertypeAll', {
            data
          }),
        notUseAllSearch: (data: I.PacketUserStatusSearch) =>
          this.post<I.PacketUserStatusSearch, I.UserNotUseAllData[]>('/api/User/UserNotUseAll', {
            data
          }),
        statusCompanySearch: (data: I.PacketUserStatusSearch) =>
          this.post<I.PacketUserStatusSearch, I.UserStatusCompanyData[]>(
            '/api/User/UserStatusCompany',
            {
              data
            }
          ),
        typeCompanySearch: (data: I.PacketUserStatusSearch) =>
          this.post<I.PacketUserStatusSearch, I.UserTypeCompanyData[]>(
            '/api/User/UserTypeCompany',
            {
              data
            }
          )
      },
      monitor: {
        groupInfo: (groupID: number) =>
          this.get<{}, I.MonitorGroup>('/api/Monitor/MonitorGroup?groupID=' + groupID),
        elementUpdate: (data: I.PacketMonitorElementUpdate) =>
          this.post<I.PacketMonitorElementUpdate, I.MonitorGroup>('/api/Monitor/ElementUpdate', {
            data
          }),
        groupPageInfo: () => this.get<{}, I.MonitoringPageInfo>('/api/Monitor/PageInfo'),
        groupUpdate: (data: I.PacketMonitorUpdate) =>
          this.post<I.PacketMonitorUpdate, I.PacketMonitorUpdate>('/api/Monitor/MonitorUpdate', {
            data
          }),
        map: (formData: FormData) => {
          this.postFile<I.MonitorGroup>('/api/Monitor/Map', formData);
        }
      },
      event: {
        get: () => this.get<{}, I.EventSearchPageInfo>('/api/Event/Search'),
        search: (data: I.PacketEventSearch) =>
          this.post<I.PacketEventSearch, I.EventAccessList>('/api/Event/Search', { data }),
        doorSearch: (data: I.PacketDoorEventSearch) =>
          this.post<I.PacketDoorEventSearch, I.EventDoorList[]>('/api/Event/DoorSearch', {
            data
          }),
        sensorSearch: (data: I.PacketSensorEventSearch) =>
          this.post<I.PacketSensorEventSearch, I.EventSensorList[]>('/api/Event/SensorSearch', {
            data
          }),
        systemSearch: (data: I.PacketSystemEventSearch) =>
          this.post<I.PacketSystemEventSearch, I.EventSystemList[]>('/api/Event/SystemSearch', {
            data
          })
      },
      config: {
        schedule: () => this.get<{}, I.UserScheduleList[]>('/api/Config/Schedule'),
        scheduleUpdate: (data: I.PacketUserScheduleUpdate) =>
          this.post<I.PacketUserScheduleUpdate, I.UserScheduleList[]>(
            '/api/Config/ScheduleUpdate',
            {
              data
            }
          ),
        acuPageInfo: () => this.get<{}, I.AcuPageInfo>('/api/Config/AcuPageInfo'),
        acuHealthPageInfo: () => this.get<{}, I.AcuHealthPageInfo>('/api/Config/AcuHealth')
      },
      account: {
        get: () => this.get<{}, I.AccountPageInfo>('/api/Config/AccountPageInfo'),
        accountUpdate: (data: I.AccountUpdateReq) =>
          this.post<I.AccountUpdateReq, I.AccountUpdateReq>('/api/config/accountupdate', {
            data
          })
      },
      sensor: {
        get: () => this.get<{}, I.SensorPageInfo>('/api/Config/SensorPageInfo')
      },
      door: {
        get: () => this.get<{}, I.DoorPageInfo>('/api/Config/Door')
      },
      code: {
        get: () => this.get<{}, I.CodePageInfo>('/api/Config/Code'),
        codeUpdate: (data: I.CodeUpdateReq) =>
          this.post<I.CodeUpdateReq, I.CodeData>('/api/config/codeupdate', {
            data
          })
      },
      group: {
        get: () => this.get<{}, I.GroupPageInfo>('/api/Auth/Group'),
        groupUpdate: (data: I.GroupUpdateReq) =>
          this.post<I.GroupUpdateReq, I.GroupSingle>('/api/Auth/GroupUpdate', {
            data
          })
      },
      antipass: {
        get: () => this.get<{}, I.AntipassPageInfo>('/api/Config/Antipass'),
        antipassUpdate: (data: I.AntipassUpdateReq) =>
          this.post<I.AntipassUpdateReq, I.AntipassDetail>('/api/config/AntipassUpdate', {
            data
          })
      },
      zone: {
        get: () => this.get<{}, I.ZonePageInfo>('/api/Config/Zone'),
        zoneUpdate: (data: I.ZoneUpdateReq) =>
          this.post<I.ZoneUpdateReq, I.ZoneDetail>('/api/config/ZoneUpdate', {
            data
          })
      },
      fire: {
        get: () => this.get<{}, I.FirePageInfo>('/api/Config/Fire'),
        fireUpdate: (data: I.FireUpdateReq) =>
          this.post<I.FireUpdateReq, I.FireDetail>('/api/config/FireUpdate', {
            data
          })
      },
      holiday: {
        get: () => this.get<{}, I.HolidayPageInfo>('/api/Config/Holiday'),
        holidayUpdate: (data: I.HolidayUpdateReq) =>
          this.post<I.HolidayUpdateReq, I.Holiday>('/api/config/HolidayUpdate', {
            data
          })
      },
      register: (data: I.UserRegisterReq) =>
        this.post<I.UserRegisterReq, I.UserRegisterResult>('/api/User/Register', {
          data
        }),
      excel: {
        authPageInfo: () => this.get<{}, I.ExcelAuthPageInfo>('/api/Excel/AuthPageInfo'),
        authRead: (formData: FormData) =>
          this.postFile<I.ExcelUserData[]>('/api/Excel/AuthRead', formData),
        userPageInfo: () => this.get<{}, I.ExcelUserPageInfo>('/api/Excel/UserPageInfo'),
        userRead: (formData: FormData) =>
          this.postFile<I.ExcelUserData[]>('/api/Excel/UserRead', formData),
        userSave: (data: I.PacketUserSave) =>
          this.post<I.PacketUserSave, I.User[]>('/api/Excel/UserSave', {
            data
          }),
        authSave: (data: I.PacketAuthSave) =>
          this.post<I.PacketAuthSave, I.User[]>('/api/Excel/AuthSave', {
            data
          })
      },
      meal: {
        get: () => this.get<{}, I.MealSearchPageInfo>('/api/Meal/Search'),
        empSearch: (data: I.PacketMealSearch) =>
          this.post<I.PacketMealSearch, I.PersonalEventList>('/api/Meal/PersonalEvent', {
            data
          }),
        companySearch: (data: I.PacketMealSearch) =>
          this.post<I.PacketMealSearch, I.CompanyEventList>('/api/Meal/CompanyEvent', {
            data
          }),
        cornerSearch: (data: I.PacketMealSearch) =>
          this.post<I.PacketMealSearch, I.CornerEventList>('/api/Meal/CornerEvent', {
            data
          }),
        daySearch: (data: I.PacketMealSearch) =>
          this.post<I.PacketMealSearch, I.DayEventList>('/api/Meal/DayEvent', {
            data
          }),
        mealSearch: (data: I.PacketMealSearch) =>
          this.post<I.PacketMealSearch, I.MealEventList>('/api/Meal/MealEvent', {
            data
          }),
        errorSearch: (data: I.PacketMealSearch) =>
          this.post<I.PacketMealSearch, I.ErrorEventList>('/api/Meal/ErrorEvent', {
            data
          }),
        configGet: () => this.get<{}, I.MealSettingPageInfo>('/api/Meal/SettingSearch'),
        mealTimeUpdate: (data: I.PacketMealTimeUpdate) =>
          this.post<I.PacketMealTimeUpdate, I.MealSettingPageInfo>('/api/Meal/MealTimeUpdate', {
            data
          }),
        cashGet: () => this.get<{}, I.MealCashPageInfo>('/api/Meal/Cash'),
        mealCardSearch: (data: I.PacketMealCardSearch) =>
          this.post<I.PacketMealCardSearch, I.MealCardData[]>('/api/Meal/MealCardEvent', {
            data
          }),
        mealCashSearch: (data: I.PacketMealCashSearch) =>
          this.post<I.PacketMealCashSearch, I.MealCashData[]>('/api/Meal/MealCashEvent', {
            data
          }),
        mealUserSearch: (search: string) =>
          this.get<{}, I.MealUserSearchData[]>('/api/Meal/MealUserSearch?search=' + search),

        mealCardSave: (data: I.PacketMealCardSave) =>
          this.post<I.PacketMealCardSave, I.MealCardData[]>('/api/Meal/MealCardSave', {
            data
          }),
        mealCashSave: (data: I.PacketMealCashSave) =>
          this.post<I.PacketMealCashSave, I.MealCashData[]>('/api/Meal/MealCashSave', {
            data
          }),
        mealEventCollect: (data: I.PacketMealCollect) =>
          this.post<I.PacketMealCollect, I.PacketMealCollect>('/api/Meal/MealEventCollect', {
            data
          })
      },
      attend: {
        get: () => this.get<{}, I.AttendSearchPageInfo>('/api/Attend/Search'),
        attendSearch: (data: I.PacketAttendanceSearch) =>
          this.post<I.PacketAttendanceSearch, I.AttendList[]>('/api/Attend/Search', {
            data
          })
      },
      other: {
        get: () => this.get<{}, I.LogPageInfo>('/api/Other/LogPageInfo'),
        logSearch: (data: I.PacketLogSearch) =>
          this.post<I.PacketLogSearch, I.LogList[]>('/api/Other/LogSearch', {
            data
          })
      },
      temp: {
        staffGet: (search: string) =>
          this.get<{}, I.TempStaffHistoryList[]>('/api/TempCard/StaffSearch?param=' + search),
        generalGet: (search: string) =>
          this.get<{}, I.TempGeneralHistoryList[]>('/api/TempCard/GeneralSearch?param=' + search),
        tempEmployeeSearch: (search: string) =>
          this.get<{}, I.TempEmployee[]>('/api/TempCard/TempEmployeeSearch?search=' + search),

        tempCardSearch: (search: string) =>
          this.get<{}, I.TempEmployee[]>('/api/TempCard/TempCardSearch?search=' + search),
        tempStaffSave: (data: I.PacketTempStaffIssue) =>
          this.post<I.PacketTempStaffIssue, I.PacketTempStaffIssue>(
            '/api/TempCard/TempStaffIssue',
            {
              data
            }
          ),
        tempStaffHistory: (data: I.PacketTempStaffHistorySearch) =>
          this.post<I.PacketTempStaffHistorySearch, I.TempStaffHistoryList[]>(
            '/api/TempCard/StaffHistorySearch',
            {
              data
            }
          ),
        tempGeneralSave: (data: I.PacketTempGeneralIssue) =>
          this.post<I.PacketTempGeneralIssue, I.PacketTempGeneralIssue>(
            '/api/TempCard/TempGeneralIssue',
            {
              data
            }
          ),
        tempGeneralHistory: (data: I.PacketTempStaffHistorySearch) =>
          this.post<I.PacketTempStaffHistorySearch, I.TempGeneralHistoryList[]>(
            '/api/TempCard/GeneralHistorySearch',
            {
              data
            }
          ),
        tempByUserId: (userid: number) =>
          this.get<{}, I.PacketTempUser>('/api/TempCard/SearchByUserID?userID=' + userid)
      }
    };
  }
}
